import { Box, Button, Stack, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import { HangarPrintLayout } from "../../../screens/Hangar/HangarPrintLayout";
import { RampPrintLayout } from "../../../screens/Ramp/RampPrintLayout";
import { Hangar, Location, Ramp, Stack as StackType } from "../../../types";
import { ConfirmationDialog } from "../../ConfirmationDialog";
import { Layout } from "../../Layout";

type Props = {
  location: Location<Ramp | Hangar>;
  favorite: StackType;
  onUse: (favorite: StackType) => void;
  onDelete: (favorite: StackType) => void;
};

export const FavoritePresenter: React.FC<Props> = ({
  location,
  favorite,
  onUse,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [confirm, setConfirm] = React.useState<{
    open: boolean;
    text: string;
    okText: string;
    onOk: () => void;
  } | null>({
    open: false,
    text: "",
    okText: "",
    onOk: () => {},
  });
  const [print, setPrint] = React.useState<boolean>(false);
  const printComponentRef = React.useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printComponentRef,
  });

  return (
    <Box
      display="flex"
      alignContent="center"
      justifyContent="center"
      alignItems="center"
      sx={{
        p: 1,
        height: 300,
        width: 300,
      }}
    >
      <Stack direction="column" spacing={0}>
        <Typography textAlign="center" variant="subtitle2">
          {favorite.name}
        </Typography>
        <Layout
          stack={favorite}
          setStack={() => {
            // not needed
          }}
          location={location}
          height={location.width / location.depth > 1.75 ? 175 : 250}
          zoomLevel={0.25}
          options={{
            hideUnplaced: true,
            readOnly: true,
            selectable: false,
            printable: false,
          }}
        />
        {print && (
          <Box
            sx={{
              position: "absolute",
              top: "100vh",
            }}
          >
            <div
              ref={printComponentRef}
              style={{
                display: "block",
                padding: 15,
                marginLeft: 50,
              }}
            >
              {location.type === "ramp" ? (
                <RampPrintLayout
                  ramp={location as Ramp}
                  stack={favorite}
                  subtitle={t('widgets.favoritesButton.singleFavorite.printSubtitle', { name: favorite.name })}
                />
              ) : (
                <HangarPrintLayout
                  hangar={location as Hangar}
                  stack={favorite}
                  subtitle={t('widgets.favoritesButton.singleFavorite.printSubtitle', { name: favorite.name })}
                />
              )}
            </div>
          </Box>
        )}
        <Stack direction="row" spacing={1} justifyContent="center" mt={1}>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setConfirm({
                open: true,
                text: t('widgets.favoritesButton.singleFavorite.confirmations.useText', { locationType: location.type, name: favorite.name }),
                okText: t('widgets.favoritesButton.singleFavorite.confirmations.useOk'),
                onOk: () => {
                  onUse(favorite);
                },
              });
            }}
          >
            {t('widgets.favoritesButton.singleFavorite.buttons.use')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setConfirm({
                open: true,
                text: t('widgets.favoritesButton.singleFavorite.confirmations.deleteText', { name: favorite.name }),
                okText: t('widgets.favoritesButton.singleFavorite.confirmations.deleteOk'),
                onOk: () => {
                  onDelete(favorite);
                },
              });
            }}
          >
            {t('widgets.favoritesButton.singleFavorite.buttons.delete')}
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={() => {
              setPrint(true);
              setTimeout(() => {
                handlePrint();
                setPrint(false);
              }, 1000);
            }}
          >
            {t('widgets.favoritesButton.singleFavorite.buttons.print')}
          </Button>
        </Stack>
      </Stack>
      {confirm?.open && (
        <ConfirmationDialog
          title={t('widgets.favoritesButton.singleFavorite.confirmations.title')}
          onCancel={() => setConfirm(null)}
          onClose={() => setConfirm(null)}
          {...confirm}
        />
      )}
    </Box>
  );
};
